import { useState, useEffect, useCallback } from 'react';
import { LocalParticipant, Room, Track } from 'livekit-client';

export interface MediaDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

export interface DeviceSelectionState {
  cameras: MediaDevice[];
  microphones: MediaDevice[];
  selectedCameraId: string | null;
  selectedMicrophoneId: string | null;
  isLoading: boolean;
  error: string | null;
}

interface UseDeviceSelectionProps {
  localParticipant: LocalParticipant | null;
  room: Room | null;
  videoRefs?: any; // VideoElementRefs type
  isLiveStreaming?: boolean; // Add streaming status
}

export const useDeviceSelection = ({
  localParticipant,
  room,
  videoRefs,
  isLiveStreaming = false
}: UseDeviceSelectionProps) => {
  const [state, setState] = useState<DeviceSelectionState>({
    cameras: [],
    microphones: [],
    selectedCameraId: null,
    selectedMicrophoneId: null,
    isLoading: false,
    error: null,
  });

  // Debug logging
  console.log('useDeviceSelection: received props:', {
    hasLocalParticipant: !!localParticipant,
    hasRoom: !!room,
    localParticipantSid: localParticipant?.sid,
    roomState: room?.state,
  });

  // Get available media devices
  const getMediaDevices = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Check if MediaDevices API is available
      if (!navigator.mediaDevices) {
        throw new Error('MediaDevices API is not available. This may be due to an insecure context (HTTP instead of HTTPS).');
      }

      // Request permission to access media devices
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

      // Get all media devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      const cameras = devices
        .filter(device => device.kind === 'videoinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
          kind: device.kind as MediaDeviceKind,
        }));

      const microphones = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: device.kind as MediaDeviceKind,
        }));

      // Set default selections (first available device)
      const selectedCameraId = cameras.length > 0 ? cameras[0].deviceId : null;
      const selectedMicrophoneId = microphones.length > 0 ? microphones[0].deviceId : null;

      setState(prev => ({
        ...prev,
        cameras,
        microphones,
        selectedCameraId,
        selectedMicrophoneId,
        isLoading: false,
      }));

      return { cameras, microphones, selectedCameraId, selectedMicrophoneId };
    } catch (error) {
      console.error('Error getting media devices:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to access media devices',
      }));
      return { cameras: [], microphones: [], selectedCameraId: null, selectedMicrophoneId: null };
    }
  }, []);

  // Switch camera device
  const switchCamera = useCallback(async (deviceId: string) => {
    console.log('switchCamera called with deviceId:', deviceId);
    if (!localParticipant || !room) {
      console.warn('Cannot switch camera: no local participant or room');
      return;
    }

    // Check if MediaDevices API is available
    if (!navigator.mediaDevices) {
      setState(prev => ({
        ...prev,
        error: 'MediaDevices API is not available. This may be due to an insecure context (HTTP instead of HTTPS).',
        isLoading: false,
      }));
      return;
    }

    // Note: Camera switching is now allowed during live streaming

    try {
      console.log('Starting camera switch...');
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Get video track with new device
      const videoTrack = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });

      // Stop current video track
      const videoPublications = Array.from(localParticipant.videoTrackPublications.values());
      for (const publication of videoPublications) {
        if (publication.source === Track.Source.Camera && publication.track) {
          console.log('Stopping current camera track');
          publication.track.stop();
          await localParticipant.unpublishTrack(publication.track);
        }
      }

      // Publish new video track
      console.log('Publishing new camera track');
      await localParticipant.publishTrack(videoTrack.getVideoTracks()[0], {
        source: Track.Source.Camera,
      });

      // Manually attach the new track to the video element
      console.log('Manually attaching new camera track to video element');
      if (videoRefs?.local?.current) {
        // Get the MediaStreamTrack and attach it directly to the video element
        const mediaStreamTrack = videoTrack.getVideoTracks()[0];
        videoRefs.local.current.srcObject = new MediaStream([mediaStreamTrack]);
        console.log('New camera track attached successfully');
      } else {
        console.log('Video ref not available for manual attachment');
      }

      setState(prev => ({
        ...prev,
        selectedCameraId: deviceId,
        isLoading: false,
      }));
      console.log('Camera switch completed successfully');
    } catch (error) {
      console.error('Error switching camera:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to switch camera',
      }));
    }
  }, [localParticipant, room]);

  // Switch microphone device
  const switchMicrophone = useCallback(async (deviceId: string) => {
    console.log('switchMicrophone called with deviceId:', deviceId);
    if (!localParticipant || !room) {
      console.warn('Cannot switch microphone: no local participant or room');
      return;
    }

    // Check if MediaDevices API is available
    if (!navigator.mediaDevices) {
      setState(prev => ({
        ...prev,
        error: 'MediaDevices API is not available. This may be due to an insecure context (HTTP instead of HTTPS).',
        isLoading: false,
      }));
      return;
    }

    // Note: Microphone switching is now allowed during live streaming

    try {
      console.log('Starting microphone switch...');
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Get audio track with new device
      const audioTrack = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: { deviceId: { exact: deviceId } },
      });

      // Stop current audio track
      const audioPublications = Array.from(localParticipant.audioTrackPublications.values());
      for (const publication of audioPublications) {
        if (publication.source === Track.Source.Microphone && publication.track) {
          console.log('Stopping current microphone track');
          publication.track.stop();
          await localParticipant.unpublishTrack(publication.track);
        }
      }

      // Publish new audio track
      console.log('Publishing new microphone track');
      await localParticipant.publishTrack(audioTrack.getAudioTracks()[0], {
        source: Track.Source.Microphone,
      });

      setState(prev => ({
        ...prev,
        selectedMicrophoneId: deviceId,
        isLoading: false,
      }));
      console.log('Microphone switch completed successfully');
    } catch (error) {
      console.error('Error switching microphone:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to switch microphone',
      }));
    }
  }, [localParticipant, room]);

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = () => {
      getMediaDevices();
    };

    // Only add event listener if MediaDevices API is available
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

      return () => {
        navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
      };
    }
  }, [getMediaDevices]);

  // Initialize devices when component mounts
  useEffect(() => {
    getMediaDevices();
  }, [getMediaDevices]);

  return {
    ...state,
    switchCamera,
    switchMicrophone,
    refreshDevices: getMediaDevices,
  };
};
